// @flow
import React, { useCallback, useState, useMemo } from 'react';
import { withApollo, WithApolloClient } from 'react-apollo';
import { uniq } from 'lodash';
import CustomAsyncSelect, { CustomAsyncSelectProps } from './CustomAsyncSelect';
import { VIRTUAL_LABELS_QUERY_PAGINATED } from '../../graphql/queries';

export const parseLabelToOptions = (labels) => {
    return (labels || [])
        .map((l) => ({
            label: l.name,
            value: l.name.toLowerCase().replace(/\W/g, ''),
            id: l.id,
        }))
        .sort((a, b) => {
            return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
        });
};

export const parseLabelToOptionsWithIdAsValue = (labels) => {
    return (labels || [])
        .map((l) => ({
            label: l.name,
            value: l.id,
        }))
        .sort((a, b) => {
            return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
        });
};

export const loadLabelsAndParseToOptions = async (
    client: ApolloClient,
    searchText: string,
    excludeLabelIds?: string[],
    labelsToOptionsParser: (labels: any) => any,
) => {
    const queryResult = await client.query({
        query: VIRTUAL_LABELS_QUERY_PAGINATED,
        variables: {
            filter: { name: searchText, excludeLabelIds },
            pagination: { pageSize: searchText ? 50 : 10, page: 0 },
        },
        fetchPolicy: 'network-only',
    });
    return labelsToOptionsParser(queryResult.data.virtualLabelsPaginated);
};

export const loadLabelsOptions = async (client: ApolloClient, searchText: string, excludeLabelIds?: string[]) => {
    return loadLabelsAndParseToOptions(client, searchText, excludeLabelIds, parseLabelToOptions);
};

export const loadLabelsOptionsWithIdAsValue = async (
    client: ApolloClient,
    searchText: string,
    excludeLabelIds?: string[],
) => {
    return loadLabelsAndParseToOptions(client, searchText, excludeLabelIds, parseLabelToOptionsWithIdAsValue);
};

export interface LabelSelectProps extends CustomAsyncSelectProps {
    excludeLabelIds?: string[];
}

const LabelSelect = ({ client, excludeLabelIds, onChange, ...props }: WithApolloClient<LabelSelectProps>) => {
    const [selectedOptionsIds, setSelectedOptionsIds] = useState(
        Array.isArray(props.value) ? props.value.filter(({ id }) => id).map(({ id }) => id) : [],
    );

    const excludeLabelIdsAndSelectedOptionsIds = useMemo(
        () => uniq([...selectedOptionsIds, ...(excludeLabelIds ?? [])]),
        [excludeLabelIds, selectedOptionsIds],
    );

    const onChangeHandler = useCallback(
        (newValue: any, actionMeta: ActionMeta<any>) => {
            setSelectedOptionsIds(Array.isArray(newValue) ? newValue.filter(({ id }) => id).map(({ id }) => id) : []);
            return onChange?.(newValue, actionMeta);
        },
        [onChange],
    );

    const loadOptions = useCallback(
        async (searchText: string) => {
            return loadLabelsOptions(client, searchText, excludeLabelIdsAndSelectedOptionsIds);
        },
        [client, excludeLabelIdsAndSelectedOptionsIds],
    );

    return <CustomAsyncSelect loadOptions={loadOptions} cacheOptions onChange={onChangeHandler} {...props} />;
};

export default withApollo(LabelSelect);
